import { z } from 'zod';

/**
 * Why one member is reporting another.
 *
 * A **closed list**, not free text, and the reasons are the product's own: a
 * reviewer works a queue by reason, `moderation.autoHideReportThreshold` counts
 * reports without reading them, and both clients build their copy from the code
 * (`t(`reason.${code}`)`) so neither ever ships an English sentence.
 *
 * The list is deliberately short. Every additional reason is one more decision a
 * distressed member has to make before they can get away from somebody, and the
 * reviewer sees the profile, the photos and the conversation regardless — the
 * code is a routing hint, not the evidence.
 *
 * - `fake_profile` — impersonation, stolen photos, a profile that is not a person.
 * - `harassment` — abuse, threats, persistent unwanted contact.
 * - `inappropriate_photos` — imagery that breaks the platform's own rules.
 * - `scam` — solicitation, advertising, money.
 * - `underage` — the one reason that is a legal obligation rather than a
 *   preference, and the reason a reviewer treats before the others.
 * - `off_platform_contact` — pushing the conversation somewhere the platform's
 *   protections do not reach, which for this product is a safety matter and not
 *   merely a rule.
 * - `other` — the catch-all, and the only code that {@link requiresReportNote}.
 */
export const REPORT_REASON_CODES = [
  'fake_profile',
  'harassment',
  'inappropriate_photos',
  'scam',
  'underage',
  'off_platform_contact',
  'other',
] as const;

export const ReportReasonCodeSchema = z.enum(REPORT_REASON_CODES);

export type ReportReasonCode = (typeof REPORT_REASON_CODES)[number];

/**
 * Does this reason oblige the reporter to say more?
 *
 * Only `other` does. Every named reason already tells the reviewer what to look
 * for, and demanding a sentence alongside one would put a writing task between a
 * member and getting away from somebody. `other` without a note is the report
 * that cannot be actioned at all, so it is refused at the contract
 * ({@link ReportCreateSchema}) rather than discovered in the queue.
 */
export function requiresReportNote(reason: ReportReasonCode): boolean {
  return reason === 'other';
}
